import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  Autocomplete,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useEffect, useState, memo } from 'react';
import { useInView } from 'react-intersection-observer';


type Dealer = {
  dealerCompanyId: number;
  dealerCompanyName: string;
  imageUrl: string;
  dealerPackageId: number;
  packageName: string;
  showDealerInVehicleList: boolean;
  showDealerCompanyLogo: boolean;
};

type Props = {
  dealerData: Dealer[];
  title?: string;
  adSpaceList?: any[]; // ads from API
};

const DealerCard = ({ dealer }: { dealer: Dealer }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={dealer.dealerCompanyId}>
      <Box
        ref={ref}
        component={RouterLink}
        to={`/dealer-vehicles/${dealer.dealerCompanyId}`}
        sx={{
          width: '100%',
          height: 80,
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s ease-in-out',
          textDecoration: 'none',
          '&:hover': {
            transform: 'scale(1.03)',
            cursor: 'pointer',
            boxShadow: 1,
          },
        }}
      >
        {inView ? (
          <Box
            component="img"
            src={dealer.imageUrl}
            alt={dealer.dealerCompanyName}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              p: 1,
            }}
            loading="lazy"
          />
        ) : null}
      </Box>
    </Grid>
  );
};

const DealerListSection = memo(({ dealerData, title = 'Our Partner Dealers', adSpaceList = [] }: Props) => {
  const navigate = useNavigate();
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);

  const visibleDealers = dealerData
  .filter(dealer => dealer.showDealerCompanyLogo) // only those with showDealerCompanyLogo true
  .slice(0, 10); // limit to 10

  // Preload dealer logos
  useEffect(() => {
    visibleDealers.forEach((dealer) => {
      const img = new Image();
      img.src = dealer.imageUrl;
    });
  }, [visibleDealers]);

  // Navigate when selecting from autocomplete
  useEffect(() => {
    if (selectedDealer) {
      navigate(`/dealer-vehicles/${selectedDealer.dealerCompanyId}`);
    }
  }, [selectedDealer, navigate]);

  // Filter ads for homepage-dealer
  const dealerAds = adSpaceList.filter(ad => ad.adPositionCode === 'homepage-dealer');

  return (
    <Box sx={{ py: 5 }} maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Autocomplete
            options={dealerData}
            getOptionLabel={(option) => option.dealerCompanyName}
            onChange={(_, value) => setSelectedDealer(value)}
            renderInput={(params) => (
              <TextField {...params} label="Choose Dealer" size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  component="img"
                  src={option.imageUrl}
                  alt={option.dealerCompanyName}
                  sx={{
                    width: 24,
                    height: 24,
                    objectFit: 'contain',
                    mr: 1,
                  }}
                />
                {option.dealerCompanyName}
              </li>
            )}
            sx={{ minWidth: 240 }}
          />

          <Button size="small" onClick={() => navigate('/dealer-vehicles')}>
            View All
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* LEFT: Dealer List */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Grid container spacing={2}>
            {visibleDealers.map((dealer) => (
              <DealerCard key={dealer.dealerCompanyId} dealer={dealer} />
            ))}
          </Grid>
        </Grid>
        
        {/* RIGHT: Dealer Ads */}
        <Grid size={{ xs: 12, md: 4 }}>
          {dealerAds.length > 0 ? (
            <Box
              sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                mx: 'auto',
              }}
            >
              <a
                href={dealerAds[0].adSpaceThumbnailUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', width: '100%' }}
              >
                <Box
                  component="img"
                  src={dealerAds[0].adSpaceImageUrl}
                  alt={dealerAds[0].name || 'Dealer Advertisement'}
                  sx={{
                    width: '100%',                // full column width
                    height: 'auto',               // adjust automatically
                    aspectRatio: '320 / 150',     // or whatever backend ratio
                    objectFit: 'contain',         // no cropping
                    display: 'block',
                  }}
                />
              </a>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: '#fffef5',
                borderRadius: 2,
                border: '2px dashed #f0c000',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 2,
                py: 3,
                boxShadow: 'inset 0 0 0 1px rgba(240,192,0,0.2)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#d17b00',
                  fontWeight: 'bold',
                  mb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                Sponsored
              </Typography>

              <Typography variant="body2" color="text.secondary">
                This space is reserved for advertisements or special dealer promotions.
              </Typography>
            </Box>
          )}
        </Grid>

      </Grid>
    </Box>
  );
});

export default DealerListSection;
